import { useEffect, useState } from 'react'
import ww1 from '../../assets/wework1.avif'
import ww2 from '../../assets/wework2.avif'
import ww3 from '../../assets/wework3.avif'
import ww4 from '../../assets/wework4.avif'
import ww5 from '../../assets/wework5.avif'

import { motion } from 'framer-motion'


export default function HowWork() {
    // Detect screen >= sm (640px)
    const cards = [
        {
            id: 1,
            image: ww1,
            number: "01",
            title: "Discover Business Requirements",
            desc: "We begin by understanding business goals, users, and technical constraints. This phase ensures we solve the right problem before investing in technology.",
        },
        {
            id: 2,
            image: ww2,
            number: "02",
            title: "Design System Architecture",
            desc: "Insights are translated into clear system architecture, workflows, and experience design. We focus on scalability, security, and real-world usability from the start.",
        },
        {
            id: 3,
            image: ww3,
            number: "03",
            title: "Engineer Scalable Solutions",
            desc: "Our teams build robust, high-performance systems using modern engineering practices. Quality, reliability, and maintainability guide every development decision.",
        },
        {
            id: 4,
            image: ww4,
            number: "04",
            title: "Deploy Reliable Platforms",
            desc: "Solutions are deployed with structured testing, validation, and performance checks. We ensure stability, security, and readiness for real business environments.",
        },
        {
            id: 5,
            image: ww5,
            number: "05",
            title: "Optimize For Continuous Growth",
            desc: "Post-launch, we monitor systems, refine performance, and evolve capabilities. This ensures platforms remain efficient, adaptable, and future-ready.",
        },
    ];

    const useIsMobile = () => {
        const [isMobile, setIsMobile] = useState(false);

        useEffect(() => {
            const media = window.matchMedia("(max-width: 639px)");
            setIsMobile(media.matches);

            const handler = (e) => setIsMobile(e.matches);
            media.addEventListener("change", handler);

            return () => media.removeEventListener("change", handler);
        }, []);

        return isMobile;
    };

    const isMobile = useIsMobile();

    if (isMobile) {
        return (

            <section>
                <div className="mx-auto max-w-7xl px-4  md:px-6">

                    <h2 className="font-bold text-2xl md:text-3xl lg:text-4xl mt-6">
                        How We Work
                    </h2>

                    <p className="font-secondary text-black font-medium text-base md:text-lg lg:text-xl leading-relaxed py-4">
                        A structured, product-first approach designed for clarity, quality, and scale.
                    </p>
                    <div className='mx-4'>
                        {cards.map(cars => (
                            <div className="relative w-[330px] h-[400px] rounded-2xl overflow-hidden my-5" key={cars.id}>
                                <img
                                    src={cars.image}
                                    alt={cars.title}
                                    className="w-full h-full object-cover"
                                    onContextMenu={(e) => e.preventDefault()}
                                    draggable={false}
                                />

                                <div className="absolute top-5 right-3">
                                    <p className="font-bold text-4xl text-black">{cars.number}</p>
                                </div>

                                <motion.div
                                    className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-3 rounded-2xl"
                                    initial={{ opacity: 0, y: 100 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.4 }}
                                    transition={{
                                        duration: 0.5,
                                        ease: "easeOut"
                                    }}
                                >
                                    <p className="font-medium text-xl text-black py-1">
                                        {cars.title}
                                    </p>

                                    <motion.p
                                        className="text-lg text-gray-700 mt-1"
                                        initial={{ opacity: 0, y: 16 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.4 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.1
                                        }}
                                    >
                                        {cars.desc}
                                    </motion.p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }
    return (
        <section className="py-8 bg-secondary-50 px-4 md:px-12 lg:px-24 mt-6">
            <div className="mx-auto max-w-7xl px-4 md:px-6">

                <h2 className="font-bold text-2xl md:text-3xl lg:text-4xl mt-6">
                    How We Work
                </h2>

                <p className="font-secondary text-black font-medium text-base md:text-lg lg:text-xl leading-relaxed py-4">
                    A structured, product-first approach designed for clarity, quality, and scale.
                </p>

                {/* TOP SECTION */}
                <div className="flex flex-col lg:flex-row lg:justify-around gap-8 items-center">

                    {/* ===== CARD 01 ===== */}
                    <motion.div
                        className="relative max-w-[500px] h-[333.33px] lg:max-w-[352px] lg:h-[620px] rounded-2xl overflow-hidden cursor-pointer"
                        initial="rest"
                        whileHover="hover"
                        animate="rest"
                    >
                        {/* IMAGE */}
                        <img
                            src={ww1}
                            alt="Discover Business Requirements"
                            className="  lg:w-full  lg:h-full object-cover"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}

                        />

                        {/* NUMBER */}
                        <div className="absolute top-5 right-3">
                            <p className="font-bold text-4xl md:text-5xl lg:text-6xl text-black">
                                01
                            </p>
                        </div>

                        {/* CONTENT */}
                        <motion.div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-3 rounded-2xl"
                            initial={{ y: 0 }}
                            whileHover={{ y: -12 }}
                            transition={{
                                type: "spring",
                                stiffness: 220,
                                damping: 5,
                                mass: 0.9
                            }}>
                            {/* TITLE */}
                            <motion.p
                                className="font-medium text-xl text-black py-1"
                                variants={{
                                    rest: { y: 0 },
                                    hover: { y: -12 }
                                }}

                            >
                                Discover Business Requirements
                            </motion.p>

                            {/* DESCRIPTION */}
                            <motion.p
                                className="font-secondary text-lg text-gray-700 mt-1 overflow-hidden"
                                variants={{
                                    rest: { y: 24, opacity: 0, height: 0 },
                                    hover: { y: 0, opacity: 1, height: "auto" }
                                }}

                            >
                                We begin by understanding business goals, users, and technical
                                constraints. This phase ensures we solve the right problem before
                                investing in technology.
                            </motion.p>
                        </motion.div>
                    </motion.div>

                    {/* ===== CENTER STACK (02 & 03) ===== */}
                    <div className="flex flex-col gap-6">

                        {/* ===== CARD 02 ===== */}
                        <motion.div
                            className="relative  max-w-[500px] lg:max-w-[432px]  h-auto xl:max-h-[294px] rounded-2xl overflow-hidden cursor-pointer"
                            initial="rest"
                            whileHover="hover"
                        // animate="rest"
                        >
                            <img src={ww2} alt="Design System Architecture" className="w-full lg:max-w-[432px] xl:max-h-[294px] lg:h-[420px] object-cover"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable={false}/>

                            <div className="absolute top-5 right-3">
                                <p className="font-bold text-4xl md:text-5xl 2xl:text-6xl text-black">02</p>
                            </div>

                            <motion.div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-3 rounded-2xl"
                                initial={{ y: 0 }}
                                whileHover={{ y: -12 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 220,
                                    damping: 5,
                                    mass: 0.9
                                }}
                            >
                                <motion.p
                                    className="font-medium text-xl text-black py-1 lg:py-0  2xl:py-1"
                                    variants={{
                                        rest: { y: 0 },
                                        hover: { y: -12 }
                                    }}

                                >
                                    Design System Architecture
                                </motion.p>

                                <motion.p
                                    className="font-secondary text-lg  text-gray-700 mt-1 lg:mt-0 2xl:mt-1"
                                    variants={{
                                        rest: { y: 24, opacity: 0, height: 0 },
                                        hover: { y: 0, opacity: 1, height: "auto" }
                                    }}

                                >
                                    Insights are translated into clear system architecture, workflows, and experience design.
                                    We focus on scalability, security, and real-world usability from the start.
                                </motion.p>
                            </motion.div>
                        </motion.div>

                        {/* ===== CARD 03 ===== */}
                        <motion.div
                            className="relative max-w-[500px] max-h-auto lg:max-w-[432px] lg:max-h-[294px] rounded-2xl overflow-hidden cursor-pointer"
                            initial="rest"
                            whileHover="hover"
                            animate="rest"
                        >
                            <img src={ww3} alt="Engineer Scalable Solutions" onContextMenu={(e) => e.preventDefault()}
                                draggable={false}
                                className="w-full h-[260px] lg:h-[320px] 2xl:h-auto object-cover" />

                            <div className="absolute top-5 right-3">
                                <p className="font-bold text-4xl md:text-5xl 2xl:text-6xl text-black">03</p>
                            </div>

                            <motion.div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-3 rounded-2xl"
                                initial={{ y: 0 }}
                                whileHover={{ y: -12 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 220,
                                    damping: 5,
                                    mass: 0.9
                                }}>
                                <motion.p
                                    className="font-medium text-xl text-black py-1"
                                    variants={{
                                        rest: { y: 0 },
                                        hover: { y: -12 }
                                    }}

                                >
                                    Engineer Scalable Solutions
                                </motion.p>

                                <motion.p
                                    className="font-secondary text-lg text-gray-700 mt-1"
                                    variants={{
                                        rest: { y: 24, opacity: 0, height: 0 },
                                        hover: { y: 0, opacity: 1, height: "auto" }
                                    }}

                                >
                                    Our teams build robust, high-performance systems using modern engineering practices.
                                    Quality, reliability, and maintainability guide every development decision.
                                </motion.p>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* ===== CARD 04 ===== */}
                    <motion.div
                        className=" cursor-pointer relative max-w-[500px] max-h-[333px] lg:max-w-[352px] lg:max-h-[620px] rounded-2xl overflow-hidden"
                        initial="rest"
                        whileHover="hover"
                        animate="rest"
                    >
                        <img
                            src={ww4}
                            alt="Deploy Reliable Platforms"
                            className=" lg:w-[392px] w-full  lg:h-[620px] object-cover"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                        />

                        <div className="absolute top-5 right-3">
                            <p className="font-bold text-4xl md:text-5xl lg:text-6xl text-black">04</p>
                        </div>

                        <motion.div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-3 rounded-2xl"
                            initial={{ y: 0 }}
                            whileHover={{ y: -12 }}
                            transition={{
                                type: "spring",
                                stiffness: 220,
                                damping: 5,
                                mass: 0.9
                            }}>
                            <motion.p
                                className="font-medium text-xl text-black py-1"
                                variants={{
                                    rest: { y: 0 },
                                    hover: { y: -12 }
                                }}

                            >
                                Deploy Reliable Platforms
                            </motion.p>

                            <motion.p
                                className="font-secondary text-lg text-gray-700 mt-1"
                                variants={{
                                    rest: { y: 24, opacity: 0, height: 0 },
                                    hover: { y: 0, opacity: 1, height: "auto" }
                                }}

                            >
                                Solutions are deployed with structured testing, validation, and performance checks.
                                We ensure stability, security, and readiness for real business environments.
                            </motion.p>
                        </motion.div>
                    </motion.div>
                </div>

                {/* ===== CARD 05 ===== */}
                <motion.div
                    className="relative  max-w-[500px] h-[333px] lg:h-auto lg:max-w-[1202px] mx-auto mt-10 rounded-2xl overflow-hidden"
                    initial="rest"
                    whileHover="hover"
                    animate="rest"
                >
                    <img src={ww5} alt="Optimize For Continuous Growth" className="w-full h-[260px] md:h-[380px] object-cover"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}/>

                    <div className="absolute top-5 right-3">
                        <p className="font-bold text-4xl md:text-5xl lg:text-6xl text-black">05</p>
                    </div>

                    <motion.div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-sm px-3 py-3 m-4 md:m-6 rounded-2xl"
                        initial={{ y: 0 }}
                        whileHover={{ y: -12 }}
                        transition={{
                            type: "spring",
                            stiffness: 220,
                            damping: 5,
                            mass: 0.9
                        }}>
                        <motion.p
                            className="font-medium text-xl text-black py-1"
                            variants={{
                                rest: { y: 0 },
                                hover: { y: -12 }
                            }}

                        >
                            Optimize For Continuous Growth
                        </motion.p>

                        <motion.p
                            className="font-secondary text-lg text-gray-700 mt-1"
                            variants={{
                                rest: { y: 24, opacity: 0, height: 0 },
                                hover: { y: 0, opacity: 1, height: "auto" }
                            }}

                        >
                            Post-launch, we monitor systems, refine performance, and evolve capabilities.
                            This ensures platforms remain efficient, adaptable, and future-ready.
                        </motion.p>
                    </motion.div>
                </motion.div>

            </div>
        </section>
    );
}

